/**
 * Resolves an adult patient's cross-visit adaptive-screener memory (E1–E3) from
 * their prior completed adaptive-screener QuestionnaireResponses: whether this
 * sitting is their baseline, and which short-check sections have opened in full
 * before (so they are asked in full again regardless of today's short check).
 *
 * @agentic connections
 */

import { useEffect, useState } from "react";
import { useMedplum } from "@medplum/react";
import type { QuestionnaireResponse } from "@medplum/fhirtypes";

import {
  ADAPTIVE_SCREENER_SESSION_SYSTEM,
  deriveFollowUpSelectionMemory,
  deriveScreenerMemoryFromResponses,
  mapResponsesToPriorSittings,
  type AdaptiveSectionKey,
} from "@benchmark/core/services";

export type AdaptiveScreenerMemory = {
  readonly isBaseline: boolean;
  readonly openedInFullBefore: ReadonlySet<AdaptiveSectionKey>;
  readonly ratingAnchorEstablished: boolean;
  /**
   * True until the patient's prior responses have resolved. Composition and
   * persistence must wait for this to be false, so a returning patient is never
   * composed or recorded with the baseline default (wrong section list / a
   * `baseline: true` session for a follow-up).
   */
  readonly loading: boolean;
  /**
   * True when the prior-responses read failed. Composition fails safe to
   * everyone-sections, but the advocate must be told history additions may be
   * incomplete (PRD degraded-composition state).
   */
  readonly degraded: boolean;
};

/** Conservative default until prior responses have loaded: treat as baseline. */
const BASELINE_MEMORY = {
  isBaseline: true,
  openedInFullBefore: new Set<AdaptiveSectionKey>(),
  ratingAnchorEstablished: false,
} as const;

/**
 * Drain every page of a search (the Medplum SDK returns one page per await), so
 * a result set larger than a single `_count` window is never silently truncated.
 */
const collectAllPages = async (
  medplum: ReturnType<typeof useMedplum>,
  query: Record<string, string>,
): Promise<QuestionnaireResponse[]> => {
  const results: QuestionnaireResponse[] = [];
  for await (const page of medplum.searchResourcePages(
    "QuestionnaireResponse",
    query,
  )) {
    results.push(...page);
  }
  return results;
};

/** Exclude the sitting currently being edited from cross-visit memory. */
export const excludeCurrentEncounterResponses = (
  responses: readonly QuestionnaireResponse[],
  encounterId: string | undefined,
): QuestionnaireResponse[] =>
  encounterId
    ? responses.filter(
        (response) =>
          response.encounter?.reference !== `Encounter/${encounterId}`,
      )
    : [...responses];

/**
 * Fetch and derive the patient's adaptive-screener memory. Returns a baseline
 * default while loading, on error, or when no patient id is available.
 *
 * @agentic connections
 */
export const useAdaptiveScreenerMemory = (
  patientId: string | undefined,
  excludeEncounterId?: string,
  enabled: boolean = true,
): AdaptiveScreenerMemory => {
  const medplum = useMedplum();
  const requestKey =
    enabled && patientId ? `${patientId}|${excludeEncounterId ?? ""}` : "";
  // Key the fetched memory by patient so a stale result is never returned for a
  // different (or absent) patient without a synchronous setState in the effect.
  const [resolved, setResolved] = useState<{
    requestKey: string;
    memory: Omit<AdaptiveScreenerMemory, "loading" | "degraded">;
    degraded: boolean;
  } | null>(null);

  useEffect(() => {
    if (!patientId || !enabled) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        // Two separate searches, each with its own _count budget, so adding
        // in-progress sittings can never EVICT completed responses from the
        // window (which would shrink the legacy floor below the shipped
        // behavior and risk un-opening history — D6/D14). The completed-only
        // query is byte-for-byte the shipped query, so the floor it produces is
        // identical to what shipped today.
        const [completed, inProgress, stopped] = await Promise.all([
          medplum.searchResources("QuestionnaireResponse", {
            patient: patientId,
            status: "completed",
            _count: 200,
            _sort: "-authored",
          }),
          // In-progress (partial) sittings so a prior partial establishes a
          // follow-up and counts toward "opened" (BMH-650 D4/§8.1). Unlike the
          // completed floor (which must stay byte-for-byte the shipped query),
          // this is new: scope it to adaptive-tagged QRs (`_tag=<system>|`
          // matches any code in that system) and drain every page, so unrelated
          // in-progress drafts can never fill a single window and evict an
          // adaptive partial — which would leave a gate that already triggered
          // un-opened at the next visit (D4/§8.1 silent-drop risk).
          collectAllPages(medplum, {
            patient: patientId,
            status: "in-progress",
            _tag: `${ADAPTIVE_SCREENER_SESSION_SYSTEM}|`,
            _count: "200",
            _sort: "-authored",
          }),
          // Stopped sittings: the remote-screener lifecycle settles declines
          // and inactivity closures as FHIR `stopped` (never a fabricated
          // `completed`), so a declined/closed administration's answers and
          // opened gates live on stopped QRs. Excluding them would un-open
          // recorded history (a GAD-7 asked then declined must stay opened)
          // and flip a started-then-closed patient back to baseline.
          collectAllPages(medplum, {
            patient: patientId,
            status: "stopped",
            _tag: `${ADAPTIVE_SCREENER_SESSION_SYSTEM}|`,
            _count: "200",
            _sort: "-authored",
          }),
        ]);
        const completedExcludingCurrent = excludeCurrentEncounterResponses(
          completed,
          excludeEncounterId,
        );
        const inProgressExcludingCurrent = excludeCurrentEncounterResponses(
          [...inProgress, ...stopped],
          excludeEncounterId,
        );
        // Legacy opened-set floor from the shipped completed-only heuristic:
        // passed to the corrected engine as the never-un-open guarantee (D14),
        // so adopting the corrected derivation can only ADD opens.
        const legacyOpenedSections = deriveScreenerMemoryFromResponses(
          completedExcludingCurrent,
        ).openedInFullBefore;
        // Corrected memory (BMH-650): "opened = the gated section's gate
        // triggered", incl. partials; unioned with the legacy floor.
        const corrected = deriveFollowUpSelectionMemory(
          mapResponsesToPriorSittings([
            ...completedExcludingCurrent,
            ...inProgressExcludingCurrent,
          ]),
          { legacyOpenedSections },
        );
        if (!cancelled) {
          setResolved({
            requestKey,
            memory: {
              isBaseline: corrected.isBaseline,
              openedInFullBefore: corrected.openedInFullBefore,
              ratingAnchorEstablished: corrected.ratingAnchorEstablished,
            },
            degraded: false,
          });
        }
      } catch {
        // Fail safe to baseline; a memory read failure must not block screening,
        // but is surfaced so the advocate knows additions may be incomplete.
        if (!cancelled) {
          setResolved({
            requestKey,
            memory: { ...BASELINE_MEMORY },
            degraded: true,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId, excludeEncounterId, enabled, requestKey, medplum]);

  // Loading only while a patient id is set but its result has not arrived.
  const isResolved = !requestKey || resolved?.requestKey === requestKey;
  const memory = isResolved && resolved ? resolved.memory : BASELINE_MEMORY;
  return {
    ...memory,
    loading: !isResolved,
    degraded: isResolved && resolved ? resolved.degraded : false,
  };
};
